import { Component, OnInit } from '@angular/core';
import { PhoneService, HistoryService } from '@wephone-app-phone/services';
import { CallDirection, ICallLog } from '@wephone-app-phone/pages/history/calllog.i';
import {
  DialogService,
  DynamicFilterSource,
  parseDateTime,
  regexSearch,
  PhoneNumberService,
  IFlexTableConfig,
  localNow,
} from '@wephone-utils';
import { CallLogDetail } from '@wephone-common/call-lists/calllog-detail/calllog-detail';
import { EntityManager } from '@wephone-core/wephone-core.module';
import { MyCallCenterStatusRepository } from '@wephone-core/model/repository/my_callcenter_status';
import { FlexBasePage } from '@wephone-core-ui';
import { NgBlockUI, BlockUI } from 'ng-block-ui';
import { ICallQualify, IQualifyResult, QualificationService } from '@wephone-common/qualification/qualification.service';
import { CallQueueRepository } from '@wephone-core/model/repository/callqueue';
import { CallLogService } from '@wephone-core/service/call_log_service';
import { VoiceMailRepository } from '@wephone-core/model/repository/voicemail';
import { BehaviorSubject, Observable } from 'rxjs';
import * as _ from 'lodash';
import { CallQueueEntity } from '@wephone-core/model/entity/callqueue';
import { _ti } from '@wephone-translation';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { FlexDatepickerMin } from '@wephone/services/form-validator';
import { FileEntryService } from '@wephone-core/service/file_entry_service';
import { SoundService } from '@wephone-audio';
import { DateTime } from 'luxon';
import { AgentPanelService } from '@wephone/services/agent-panel.service';
import { AuthenticationService } from '@wephone-core/service/authentication';

@Component({
  selector: 'calllog-list',
  templateUrl: 'calllog-list.html',
  styleUrls: ['./calllog-list.scss']
})
export class CallLogListPage extends FlexBasePage implements OnInit {
  tableConfig: IFlexTableConfig;
  dataSource: DynamicFilterSource;
  filterReady: boolean;
  filterForm: FormGroup;

  private readonly _dataSource: BehaviorSubject<ICallLog[]>;
  private readonly queues: { [key: string]: CallQueueEntity } = {};
  private queueRepo: CallQueueRepository;
  private userId: number;

  @BlockUI('flex-grid-block') blockUIList: NgBlockUI;
  constructor(
    private readonly historyService: HistoryService,
    private readonly phoneService: PhoneService,
    private readonly agentPanelService: AgentPanelService,
    private readonly qualService: QualificationService,
    private readonly calllogService: CallLogService,
    private readonly dialogService: DialogService,
    private readonly em: EntityManager,
    private readonly fb: FormBuilder,
    private readonly fileEntryService: FileEntryService,
    private readonly soundService: SoundService,
    private readonly authService: AuthenticationService,
    dateAdapter: DateAdapter<Date>,
  ) {
    super();
    this.dateAdapter = dateAdapter;
    this._dataSource = new BehaviorSubject<ICallLog[]>([]);

    this.queueRepo = this.em.getRepository<CallQueueRepository>('CallQueueRepository');
    this.userId = this.authService.getUserId();
  }

  getDataSource<T extends ICallLog = any>(): Observable<T[]> {
    return this._dataSource.asObservable() as Observable<T[]>;
  }

  ngOnInit(): void {
    super.ngOnInit();
    const ds = this.getDataSource<ICallLog>();
    this.dataSource = new DynamicFilterSource(ds, null);

    this.tableConfig = {
      rowHeight: 40,
      focusedRowHeight: 80,
      multiSelect: false,
      enableFilter: false,
      enableExportCsv: false,
      columns: [
        {
          name: 'content',
          label: '',
          searchable: false
        }
      ]
    };

    const queues: CallQueueEntity[] = this.queueRepo.getObjectList();
    for (const q of queues) {
      this.queues[q.id] = q;
    }

    // this.reloadData();
  }

  protected resolveData(): Promise<any> {
    const today: DateTime = localNow();
    this.filterForm = this.fb.group({
      start_dt: [today.minus({ days: 7 }).toJSDate()],
      end_dt: [today.toJSDate()],
      queue_ids: undefined,
      client_number: undefined,
    }, { validator: FlexDatepickerMin('end_dt', 'start_dt') });

    return this.reloadData();
  }

  /******************
  * Cannot bind input [min] because there's a bug with Mat-Datepicker,
  * the Form error won't fire if the binding object's value was changed.
  ******************/
  minEndDateFilter = (d: Date): boolean => {
    const startDate = this.filterForm.get('start_dt').value;
    if (!d || !startDate) {
      return true;
    }

    const minDate = parseDateTime(startDate)?.startOf('day');
    const dt = parseDateTime(d)?.startOf('day');
    return minDate && dt && minDate.diff(dt).as('days') > 0 ? false : true;
  }

  // get filterCount(): number {
  //   let count = 0;
  //   const filterData = this.filterForm.value;
  //   if (filterData.start_dt && filterData.end_dt) {
  //     count += 1;
  //   }
  //   if (filterData.client_number) {
  //     count += 1;
  //   }

  //   if ((filterData.queue_ids || []).length) {
  //     count += 1;
  //   }
  //   return count;
  // }

  onMakeCall = (phoneNumber: string, call: any) => {
    // this.loggerService.log('Calling number', phoneNumber);
    this.makeCall(call);
  }

  detailCall(call: ICallLog): void {
    // this.loggerService.log('Detail call', call);
    this.dialogService.openDialog2(
      CallLogDetail,
      {
        data: {
          call,
          onMakeCall: this.onMakeCall
        },
        padding: false
      }
    );
  }

  canQualify(item: any): boolean {
    return !item.is_missed && item.has_qualification;
  }

  async reQualify(call: ICallLog): Promise<void> {
    if (call.is_missed) {
      this.loggerService.error('Cannot qualify missed call');
      return;
    }

    const callQualify: ICallQualify = {
      id: call.id,
      qualification_comment: call.qualification_comment,
      out_qualification_id: call.out_qualification_id,
      in_qualification_id: call.in_qualification_id,
      queue_ids: call.queue_ids,
      is_outgoing: call.is_outgoing,
      qualification_ids: call.qualification_ids,
      // qualification: call.qualification,
    };

    const result: IQualifyResult = await this.qualService.reQualify(callQualify);
    if (result) {
      _.extend(call, {
        qualification_ids: result.qualification_ids,
        qualification_comment: result.qualification_comment,
        qualification: result.qualification,
      });
    }
  }

  isAnonymous(call: ICallLog): boolean {
    return call.user_direction === CallDirection.INBOUND && call.calling_number === 'anonymous';
  }

  makeCall(call: ICallLog): void {
    const ccConnected: boolean = this.agentPanelService.ccConnected();
    // Agent cannot make call when not connected
    if (!ccConnected) {
      this.dialogService.confirmDialog('Call Center', 'You must connect to Call Center before making a call', () => { });
      return;
    }

    const queueId = call.queue_ids ? call.queue_ids[0] : undefined;
    const remoteNumber: string = this.calllogService.getRemoteNumber(call);
    const localNumber: string = this.calllogService.getLocalNumber(call);
    this.phoneService.makeCall(remoteNumber, localNumber, queueId);
  }

  filterFunc = (item: ICallLog): boolean => {
    const filters = {
      queue_ids: this.filterForm.get('queue_ids').value || [],
      client_number: this.filterForm.get('client_number').value
    };

    if (filters.queue_ids.length === 0 && !filters.client_number) {
      return true;
    }

    const outbound = item.user_direction === CallDirection.OUTBOUND;
    const phoneNumberService = PhoneNumberService.getInstance();

    let filterClientNumber = true;
    if (filters.client_number) {
      const clientNumber = phoneNumberService.normalizeNumber(filters.client_number);
      const phoneNumber = outbound ? item.called_number : item.calling_number;

      filterClientNumber =
        regexSearch(phoneNumber, clientNumber) ||
        regexSearch(phoneNumberService.getDisplayNumber(phoneNumber), clientNumber);
    }

    let filterQueue = true;
    if (filters.queue_ids.length) {
      filterQueue = !!filters.queue_ids.filter(queue_id => _.includes(item.queue_ids, queue_id)).length;
    }

    return filterClientNumber && filterQueue;
  }

  private getDateFilters(): {
    start_dt?: DateTime,
    end_dt?: DateTime,
  } {
    const filters = {
      start_dt: this.filterForm.get('start_dt').value,
      end_dt: this.filterForm.get('end_dt').value,
    };

    if (filters.start_dt) {
      filters.start_dt = parseDateTime(filters.start_dt);
    }

    if (filters.end_dt) {
      filters.end_dt = parseDateTime(filters.end_dt);
    }

    return filters;
  }

  async reloadData(): Promise<any> {
    try {
      const message = _ti('public.message.loading');
      this.blockUIList.start(message);

      const dateFilters = this.getDateFilters();
      let filterParams: any;

      if (dateFilters && dateFilters.start_dt && dateFilters.end_dt) {
        filterParams = {
          start_dt: dateFilters.start_dt.toISODate(),
          end_dt: dateFilters.end_dt.toISODate(),
        };
      }

      const resp: ICallLog[] = await this.historyService.getCallHistory(filterParams);
      const queues: CallQueueEntity[] = this.queueRepo.getObjectList();
      const historyList: ICallLog[] = [];

      for (const item of resp) {
        if (!this.filterFunc(item)) {
          continue;
        }

        const isOutgoing: number = +item.user_direction === CallDirection.OUTBOUND ? 1 : 0;
        const historyItem: ICallLog = item;

        historyItem.is_outgoing = isOutgoing;
        historyItem.has_qualification = 0;

        if (item.queue_ids && item.queue_ids.length) {
          const queue: CallQueueEntity = queues.find(x => +item.queue_ids[0] === x.id);

          if (
            queue && queue.has_qualification &&
            ((isOutgoing && queue.out_qualification_id) || (!isOutgoing && queue.in_qualification_id))
          ) {
            historyItem.has_qualification = 1;
          }
          // const queue: CallQueueEntity = queues.find(x => _.includes(item.queue_ids, x.id) && x.has_qualification);
          // historyItem.has_qualification = queue ? 1 : 0;
        }

        historyList.push(historyItem);
      }

      this._dataSource.next(historyList);

      return 'OK';
    } catch (error) {
      this.loggerService.error('Reload data error', error);
    } finally {
      this.blockUIList.stop();
    }
  }

  getQueueName(call: ICallLog): string {
    if ((call.queue_ids || []).length === 0) {
      return undefined;
    }
    const queueId = call.queue_ids[0];
    return this.queues && this.queues[queueId] && this.queues[queueId].queue_name;
  }

  async updateListened(item: ICallLog): Promise<void> {
    try {
      if (item.is_read && _.includes(item.readers || [], this.userId)) {
        // this.loggerService.log('Voice mail has been already listened');
        return;
      }

      await this.em.getRepository<VoiceMailRepository>('VoiceMailRepository').markVoiceMailListened(item.voicemail_public_id);

      item.is_read = 1;
      if (!item.readers) {
        item.readers = [];
      }
      item.readers.push(this.userId);

      // this.cdr.markForCheck();

    } catch (error) {
      this.loggerService.error('Mark as listened error', error);
    }
  }

  clearValue(event: MouseEvent, controlName: string, multiple: boolean = false): void {
    event.stopPropagation();
    this.filterForm.get(controlName).setValue(multiple ? [] : null);
  }

  async applyFilter(): Promise<void> {
    if (!this.filterForm.valid) {
      this.filterForm.markAllAsTouched();
      return;
    }
    const ret = await this.reloadData();
    if (ret) {
      this.filterReady = false;
    }
  }

  downloadRecordedCall(item: ICallLog): void {
    this.downloadSound(item.recorded_call_public_id, 'recording_call');
  }

  downloadVoiceMessage(item: ICallLog): void {
    this.downloadSound(item.voicemail_public_id, 'voice_message');
  }

  private downloadSound(publicId: string, prefix?: string): void {
    const fileName = `${prefix || 'file'}-${publicId}.mp3`;
    const download_url = this.fileEntryService.getFileUrl(publicId, true);
    this.fileEntryService.download(download_url, fileName);
  }

  isPlayerVisible(item: ICallLog): boolean {
    const visible = this.soundService.isPlayerVisible();
    const playingData = this.soundService.getPlayingFileEntry();
    const publicId = playingData && playingData.file_public_id;
    return visible && publicId && (publicId === item.recorded_call_public_id || publicId === item.voicemail_public_id);
  }

  rendererQueue(queueIds: number[]): string[] {
    return this.queueRepo.getObjectList().filter(q => _.includes(queueIds, q.id)).map(q => q.queue_name);
  }
}
